export const LINK = "https://appdownload.flash.health/";
export const CHANNELS = ["sms", "email", "push"];
export const LANGUAGES = ["English", "Sinhala"];
export const STAGES = ["Day 1", "Day 3", "Day 5", "Day 7"];
export const SEND_DAYS = [1, 3, 5, 7];
export const STAGE_CHANNELS = ["sms", "push", "email", "sms"];
export const STAGE_TYPES = ["Promotion", "Lifestyle", "Promotion", "Lifestyle"];
export const stageLabel = stage => {
  const index = STAGES.indexOf(stage);
  return index >= 0 ? `Day ${SEND_DAYS[index]} · ${STAGE_TYPES[index]} · ${channelLabel(STAGE_CHANNELS[index])}` : stage;
};
// Missing activity is not evidence of active super-admin access.
export const canAccessCampaignStudio = user => !!user && Number(user.role) === 1 &&
  Number(user.is_active) === 1;
const segmentLabels = {
  aia_package_users: "AIA package users",
  basic_package_users: "Basic package users",
  catalogue_app_no_orders: "Catalogue-package app users with no orders",
  cashless_opd_users: "Cashless OPD users",
  new_customers: "New customers within 30 days",
  no_orders: "Customers with no orders",
  one_order: "Customers with exactly one order",
  returning_customers: "Customers with two or more orders",
  inactive_customers: "Previous orders, none within 90 days",
  recent_customers: "Customers with an order within 30 days"
};
export const segmentLabel = (segment, catalogue = {}) => (catalogue.labels || {})[segment] || segmentLabels[segment] || String(segment || "Customer group not specified").replace(/_/g, " ");
export const count = text => Array.from(text || "").length;
export const sampleText = text => (text || "").replace(/\{\{deep_link\}\}/g, LINK).replace(/\{\{lead_name\}\}/g, "Customer");
export const dayDifference = (today, start) => Math.floor((Date.parse(today.slice(0, 10)) - Date.parse(start.slice(0, 10))) / 86400000);
export const dateAfter = (start, days) => new Date(Date.parse(start.slice(0, 10)) + days * 86400000).toISOString().slice(0, 10);
export function planningDay(today, cycles) {
  const latest = cycles[0];
  if (!latest) return {
    start: today,
    slot: "A",
    day: 1
  };
  const age = dayDifference(today, latest.start_date);
  if (age >= 6) return {
    start: today,
    slot: "A",
    day: 1
  };
  return {
    cycle: latest,
    start: latest.start_date.slice(0, 10),
    slot: age % 2 === 1 ? "B" : "A",
    day: age + 1
  };
}
export function errorMessage(error) {
  const data = error.response && error.response.data;
  const fields = data && data.errors ? Object.keys(data.errors).map(k => `${k}: ${[].concat(data.errors[k]).join(" ")}`).join("\n") : "";
  return [(data && data.message) || error.message || "Request failed. Please retry.", fields].filter(Boolean).join("\n");
}
export const pendingCampaign = c => c && ["audience_building", "generating", "active"].includes(c.status);
export const metadata = log => {
  try { return typeof log.metadata_json === "string" ? JSON.parse(log.metadata_json) || {} : log.metadata_json || {}; }
  catch (_) { return {}; }
};

export const SMS_LIMIT = 160;
export const smsLength = text => count(sampleText(text));
export function overlongSms(content, settings) {
  if (!(settings.channels || []).includes("sms")) return [];
  const languages = settings.language_mode === "preferred" ? LANGUAGES : settings.languages || [];
  const errors = [];
  (content || []).forEach((stage, index) => {
    if (STAGE_CHANNELS[index] !== "sms") return;
    const variants = (stage.variants || {}).sms || {};
    languages.forEach(language => {
      if (variants[language] && smsLength(variants[language].body) > SMS_LIMIT) {
        errors.push(`${STAGES[index]} · ${language}: ${smsLength(variants[language].body)}/${SMS_LIMIT} characters`);
      }
    });
  });
  return errors;
}

// Status presentation. Tone drives color; the glyph and label carry the same
// meaning so status is never conveyed by color alone.
const STATUS_META = {
  audience_building: { label: "Preparing customers", tone: "info", glyph: "◔" },
  audience_failed: { label: "Customer selection failed", tone: "danger", glyph: "!" },
  generating: { label: "Generating", tone: "info", glyph: "◔" },
  draft: { label: "Draft", tone: "neutral", glyph: "○" },
  approved: { label: "Approved", tone: "success", glyph: "✓" },
  active: { label: "Live", tone: "success", glyph: "●" },
  paused: { label: "Paused", tone: "warning", glyph: "❙❙" },
  completed: { label: "Completed", tone: "neutral", glyph: "✓" },
  ready: { label: "Ready", tone: "success", glyph: "✓" },
  failed: { label: "Failed", tone: "danger", glyph: "!" },
  queued: { label: "Queued", tone: "info", glyph: "◔" },
  processing: { label: "Processing", tone: "info", glyph: "◔" },
  submitted: { label: "Submitted", tone: "success", glyph: "✓" },
  skipped: { label: "Skipped", tone: "neutral", glyph: "–" },
  expired: { label: "Expired", tone: "neutral", glyph: "–" },
  unknown: { label: "Unknown", tone: "warning", glyph: "?" }
};
export const statusMeta = status => STATUS_META[status] || {
  label: String(status || "Not available").replace(/_/g, " "),
  tone: "neutral",
  glyph: "○"
};

const HTTP_PREFIX = {
  401: "Your session expired. Sign in again.",
  403: "Active super-admin access is required.",
  409: "The saved notification set changed or this action is unavailable.",
  422: "Review the validation details below.",
  429: "Request limit reached. Wait a moment before retrying."
};
export function describeError(error) {
  const status = error && error.response && error.response.status;
  const prefix = HTTP_PREFIX[status] || (status >= 500 ? "The backend request failed. Ask the backend administrator to check queue and configuration." : "");
  return {
    status,
    message: [prefix, errorMessage(error)].filter(Boolean).join(" "),
    forbidden: status === 401 || status === 403
  };
}

export const formatDay = value => {
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value || "");
  return date.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
};
export const formatDate = value => {
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value || "");
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
export const slotDays = () => SEND_DAYS;
export const channelLabel = channel => ({ sms: "SMS", email: "Email", push: "Push" })[channel] || channel;
export const ACCESS_DENIED_MESSAGE = "Personalized Notifications requires an active super-admin account. Sign in again or contact the backend administrator.";

// Push tests are addressed by the app user's Sri Lankan mobile number:
// 0771234567 or +94771234567.
export const SRI_LANKAN_MOBILE = /^(?:0|\+94)7\d{8}$/;
export const isSriLankanMobile = value => SRI_LANKAN_MOBILE.test(String(value || "").trim());
// Field-level message from a Laravel-style error body, falling back to its message.
export const fieldErrorMessage = (error, field) => {
  const data = error && error.response && error.response.data;
  const messages = data && data.errors && data.errors[field];
  return messages ? [].concat(messages).join(" ") : (data && data.message) || "";
};
